// Generate a Schema.org BreadcrumbList JSON-LD fragment that Google
// rich-snippets into search-result breadcrumbs.
//
// Usage:
//   schema={{
//     "@context": "https://schema.org",
//     "@graph": [
//       { "@type": "Organization", ... },
//       breadcrumbList([["Home", "/"], ["About", "/about"]]),
//     ],
//   }}
import { SITE_URL as SITE_ORIGIN } from "@/lib/site-config";

export const breadcrumbList = (
  crumbs: Array<[label: string, path: string]>
): Record<string, unknown> => ({
  "@type": "BreadcrumbList",
  itemListElement: crumbs.map(([name, path], i) => ({
    "@type": "ListItem",
    position: i + 1,
    name,
    item: `${SITE_ORIGIN}${path}`,
  })),
});

// Map known URL path segments to a human-readable breadcrumb label. Anything
// missing falls back to a title-cased version of the segment so dynamic
// routes (CAS numbers, slugs) still render reasonable crumbs.
const SEGMENT_LABELS: Record<string, string> = {
  about: "About",
  products: "Products",
  product: "Products",                      // /product/:cas — collapse under "Products"
  "human-apis": "Human APIs",
  intermediates: "Intermediates",
  veterinary: "Veterinary APIs",
  capabilities: "Capabilities",
  quality: "Quality & Compliance",
  news: "News & Insights",
  contact: "Contact",
  checkout: "Checkout",
  auth: "Sign in",
  "privacy-policy": "Privacy Policy",
  "return-policy": "Return Policy",
  therapeutic: "Therapeutic Areas",
};

const titleCase = (s: string) =>
  s.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

/**
 * Auto-derive crumbs from a URL pathname. The home crumb is always first.
 * Examples:
 *   /                          → [Home]
 *   /products                  → [Home, Products]
 *   /products/human-apis       → [Home, Products, Human APIs]
 *   /product/26787-78-0        → [Home, Products, 26787-78-0]
 *   /therapeutic/oncology      → [Home, Therapeutic Areas, Oncology]
 *
 * Returns null for the home route so callers can decide whether to emit
 * (a single-item BreadcrumbList offers no rich-results value on Home).
 */
export const autoBreadcrumbs = (
  pathname: string,
): Array<[string, string]> | null => {
  const clean = pathname.replace(/\/+$/, "").replace(/^\/+/, "");
  if (!clean) return null; // home — skip
  const segments = clean.split("/");
  const crumbs: Array<[string, string]> = [["Home", "/"]];
  let acc = "";
  for (const seg of segments) {
    acc += `/${seg}`;
    const label = SEGMENT_LABELS[seg] ?? titleCase(seg);
    crumbs.push([label, acc]);
  }
  return crumbs;
};
